import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1764258560295-21e74c3d0d15?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function AuthPage() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const { login, signup, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        await signup(name, email, password);
        toast.success("Welcome. Let us know who you are.");
        navigate("/onboarding");
      } else {
        await login(email, password);
        toast.success("Welcome back.");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went amiss.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 py-20 bg-[#1A1A1A]">
      <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.05]" />
      <div className="absolute inset-0 bg-[#1A1A1A]/90" />

      <div className="relative z-10 w-full max-w-md goji-glass-strong p-10 sm:p-12">
        <Link to="/" className="flex justify-center mb-10" data-testid="auth-logo-link">
          <span className="font-display text-4xl font-normal text-[#F5F5F5]">Goji</span>
        </Link>

        <h1 className="font-display text-3xl sm:text-4xl font-normal text-center text-[#F5F5F5]" data-testid="auth-title">
          {mode === "signup" ? "Apply to the circle." : "Welcome back."}
        </h1>
        <p className="text-center text-sm text-[#F5F5F5]/55 mt-3 italic">
          {mode === "signup" ? "Verified members only. Considered always." : "Continue, quietly."}
        </p>

        <button
          onClick={handleGoogle}
          className="mt-10 w-full flex items-center justify-center gap-3 py-3.5 border border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors font-label text-[0.65rem] text-[#F5F5F5]"
          data-testid="auth-google-button"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#D4AF37" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#F5F5F5" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#D4AF37" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22z"/><path fill="#F5F5F5" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </button>

        <div className="my-7 flex items-center gap-3 font-label text-[0.6rem] text-[#F5F5F5]/35">
          <div className="flex-1 h-px bg-[#D4AF37]/15" />
          or
          <div className="flex-1 h-px bg-[#D4AF37]/15" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
          {mode === "signup" && (
            <div className="relative">
              <User className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full pl-11 pr-4 py-3.5 text-sm"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
            <input
              type="email"
              placeholder="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-11 pr-4 py-3.5 text-sm"
              data-testid="auth-email-input"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
            <input
              type="password"
              placeholder="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full pl-11 pr-4 py-3.5 text-sm"
              data-testid="auth-password-input"
            />
          </div>

          <button type="submit" disabled={loading} className="w-full btn-ghost flex items-center justify-center gap-2 mt-6" data-testid="auth-submit-button">
            {loading ? "..." : (mode === "signup" ? "Apply" : "Continue")}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </form>

        <p className="mt-7 text-center text-sm text-[#F5F5F5]/55">
          {mode === "signup" ? "Already in the circle? " : "New to Goji? "}
          <button
            onClick={() => setMode(mode === "signup" ? "login" : "signup")}
            className="text-[#D4AF37] hover:text-[#F5F5F5] transition-colors underline underline-offset-4 decoration-[#D4AF37]/40"
            data-testid="auth-mode-toggle"
          >
            {mode === "signup" ? "Sign in" : "Apply"}
          </button>
        </p>
      </div>
    </div>
  );
}
